/**
 * Input Group Field Component
 * Responsible for showing input level errors specific to a particular field
 *
 */
import {
  Component,
  inject,
  contentChild,
  signal,
  computed,
  Injector
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { FormField } from '@angular/forms/signals';
import { NgTemplateOutlet } from '@angular/common';
import { Subject } from 'rxjs';

import { InputErrorsComponent } from '../input-errors/input-errors.component';
import { FORM_HOST } from '../form.contract';
import { FormRowComponent } from '../row/form-row.component';
import { fieldStateFromNgModel, fieldStateFromSignalField } from '../field-state/field-state';

import type { AfterContentInit, OnDestroy } from '@angular/core';
import type { FormError } from '../domain/form.domain';
import type { FieldState } from '../field-state/field-state';

@Component({
  selector: 'td-wb-input-group-field',
  templateUrl: 'input-group-field.component.html',
  styleUrls: ['input-group-field.component.scss'],
  imports: [NgTemplateOutlet],
  host: {
    '[class.td-wb-input-group-field--narrative]': 'isNarrative'
  }
})
export class InputGroupFieldComponent implements AfterContentInit, OnDestroy {
  private readonly _contentInputControl = contentChild(NgModel);
  private readonly _contentSignalFormField = contentChild(FormField);
  private readonly _manualInputControl = signal<NgModel | undefined>(undefined);
  private readonly inputControl = computed(() => this._manualInputControl() ?? this._contentInputControl());
  private readonly inputErrors = contentChild(InputErrorsComponent);
  private statusAdapter: FieldState | null = null;
  private statusSubscription?: { unsubscribe(): void };
  private registeredRowStatusName: string | null = null;
  /**
   * Lazily built and cached per resolved source. `fieldStateFromNgModel()` calls `toSignal()`
   * internally, which Angular forbids inside a reactive (`computed`/`effect`) context — so this
   * must stay a plain method backed by an imperative cache, not a `computed()`.
   */
  private cachedFieldState?: FieldState;
  private cachedFieldStateSource?: NgModel | InstanceType<typeof FormField>;
  name: string;
  onErrorSubject: Subject<FormError>;

  private readonly formRow = inject(FormRowComponent, { host: true, optional: true });
  private readonly formHost = inject(FORM_HOST, { optional: true });
  private readonly injector = inject(Injector);

  get isNarrative(): boolean {
    return this.formHost?.formType() === 'narrative';
  }

  constructor() {
    this.onErrorSubject = new Subject<FormError>();
  }

  ngAfterContentInit(): void {
    if (this.resolveFieldState() !== undefined) {
      this.initModelChangeHandler();
    }
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
    if (this.formRow !== null && this.registeredRowStatusName !== null) {
      this.formRow.clearExternalControlStatus(this.registeredRowStatusName);
    }
  }

  getInputControl(): NgModel {
    return this.inputControl();
  }

  shouldForceDisplayError(): boolean {
    const fieldState = this.resolveFieldState();
    const inputErrors = this.inputErrors();
    if (inputErrors !== undefined && fieldState !== undefined) {
      inputErrors.getErrorToDisplay(fieldState.validationErrors(inputErrors.errorKeyNamespace() ?? ''));
      return inputErrors.shouldForceDisplay();
    }
    return false;
  }

  isSubmitted(): boolean {
    const inputErrors = this.inputErrors();
    if (inputErrors !== undefined && this.resolveFieldState() !== undefined) {
      return inputErrors.isSubmitted();
    }
    return false;
  }

  initModelChangeHandler(): void {
    this.statusSubscription?.unsubscribe();
    const fieldState = this.resolveFieldState();
    if (fieldState === undefined) {
      this.statusAdapter = null;
      return;
    }

    this.statusAdapter = fieldState;
    this.name = fieldState.name();
    const inputErrors = this.inputErrors();
    if (inputErrors !== undefined) {
      inputErrors.subscribeToStateAdapter(fieldState);
    }

    this.statusSubscription = fieldState.subscribeStatusChange((isInvalid: boolean): void => {
      if (this.statusAdapter?.dirty() || this.shouldForceDisplayError()) {
        this.toggleError(isInvalid);
      } else {
        this.toggleError(false);
      }

      if (this.formRow !== null && this.formRow.isNarrative && this.formRow.rowName() !== undefined) {
        this.registeredRowStatusName = this.name;
        this.formRow.setExternalControlStatus(
          this.name,
          this.statusAdapter?.dirty() ?? false,
          !(this.statusAdapter?.invalid() ?? false)
        );
      }
    });
  }

  registerInput(inputModel: NgModel): void {
    this._manualInputControl.set(inputModel);
    this.initModelChangeHandler();
  }

  private toggleError(value: boolean): void {
    this.onErrorSubject.next({
      name: this.name,
      value
    });
  }

  /**
   * Resolves the `FieldState` for whichever source is currently present (manually registered or
   * projected `NgModel`, else a projected `FormField`), rebuilding it only when the underlying
   * source instance actually changes. Deliberately a plain method, not a `computed()` — building
   * a `FieldState` from an `NgModel` calls `toSignal()` internally, which Angular forbids inside
   * a reactive context.
   */
  private resolveFieldState(): FieldState | undefined {
    const inputControl = this.inputControl();
    const source = inputControl ?? this._contentSignalFormField();

    if (source === undefined) {
      this.cachedFieldState = undefined;
      this.cachedFieldStateSource = undefined;
      return undefined;
    }

    if (source !== this.cachedFieldStateSource) {
      this.cachedFieldStateSource = source;
      this.cachedFieldState = inputControl !== undefined
        ? fieldStateFromNgModel(inputControl, this.injector)
        : fieldStateFromSignalField(this._contentSignalFormField());
    }

    return this.cachedFieldState;
  }
}
